// ---------------------------------------------------------------------------
// Inscrição presencial — a vaga é de quem a direção bipar primeiro.
//
// Regras (o banco já cuida de tudo):
//  · `modalidades.validacao_presencial` liga o modo presencial numa modalidade.
//  · Uma inscrição nova nessa modalidade nasce em `lista_espera` sem
//    `validada_em` = "aguardando validação". Só a bipagem dá a vaga.
//  · A ordem da fila é a ordem da bipagem.
//
// Aqui ficam só os invólucros das RPCs e os tipos. Nada de regra na tela.
// ---------------------------------------------------------------------------
import { supabase } from './supabase';

export type SituacaoPresencial =
  | 'aguardando'
  | 'vaga'
  | 'lista_espera'
  | 'cancelada';

export interface SituacaoDaInscricao {
  existe: boolean;
  presencial: boolean;
  situacao: SituacaoPresencial | null;
  ordem: number | null;
  posicao_espera: number | null;
  validada_em: string | null;
  /** Senha da fila (ex.: "M07") quando a modalidade usa senha. */
  senha: string | null;
  /** Modalidade que entra pela senha da fila (sem precisar bipar QR). */
  usa_senha: boolean;
}

export type ResultadoValidacao =
  | 'vaga'
  | 'lista_espera'
  | 'ja_validada'
  | 'nao_encontrada'
  | 'cancelada'
  | 'nao_presencial'
  | 'sem_senha';

export interface RetornoValidacao {
  ok: boolean;
  situacao: ResultadoValidacao;
  ordem: number | null;
  posicao_espera: number | null;
  nome: string | null;
  idade: number | null;
  foto_url: string | null;
  grupo: string | null;
  modalidade: string | null;
  vagas_usadas: number | null;
  vagas_total: number | null;
  mensagem: string | null;
}

export interface GrupoPresencial {
  id: number;
  nome: string;
  vagas: number | null;
  com_vaga: number;
  aguardando: number;
}

export interface ModalidadePresencial {
  id: number;
  nome: string;
  vagas_total: number | null;
  com_vaga: number;
  lista_espera: number;
  aguardando: number;
  usa_senha: boolean;
  grupos: GrupoPresencial[];
}

// Total de vagas que a tela deve mostrar. Quando a modalidade tem grupos
// (opções) com vagas, o total é a SOMA das vagas dos grupos — não o
// `vagas_total` cadastrado na modalidade (que pode ser outro número).
export function vagasDaModalidade(m: {
  grupos: { vagas: number | null }[];
  vagas_total: number | null;
}): number | null {
  const comVagas = m.grupos.filter((g) => g.vagas !== null && g.vagas !== undefined);
  if (comVagas.length > 0) {
    return comVagas.reduce((soma, g) => soma + Number(g.vagas), 0);
  }
  return m.vagas_total;
}

export interface UltimaBipagem {
  ordem: number | null;
  nome: string | null;
  grupo: string | null;
  situacao: string | null;
  hora: string | null;
  senha: string | null;
}

export interface PainelPresencial {
  modalidades: ModalidadePresencial[];
  ultimas: UltimaBipagem[];
}

export interface AguardandoLinha {
  codigo: string;
  nome: string;
  grupo: string | null;
  modalidade: string | null;
  idade: number | null;
  cpf_final: string | null;
  inscrito_as: string | null;
}

const REGEX_UUID =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Extrai o UUID de dentro do QR — aceita o código puro ou uma URL que o contenha.
export function extrairCodigo(texto: string): string | null {
  const achado = (texto || '').match(REGEX_UUID);
  return achado ? achado[0].toLowerCase() : null;
}

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function txt(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v);
  return s === '' ? null : s;
}

// `situacao_presencial` — qualquer pessoa com o código da inscrição.
export async function buscarSituacaoPresencial(
  codigo: string,
): Promise<SituacaoDaInscricao | null> {
  const { data, error } = await supabase.rpc('situacao_presencial', { p_codigo: codigo });
  if (error) throw error;
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const situacao = txt(o.situacao) as SituacaoPresencial | null;
  return {
    existe: o.existe === true,
    presencial: o.presencial === true,
    situacao,
    ordem: num(o.ordem),
    posicao_espera: num(o.posicao_espera),
    validada_em: txt(o.validada_em),
    senha: txt(o.senha),
    usa_senha: o.usa_senha === true,
  };
}

// `validar_inscricao_presencial` — só a direção do evento.
export async function validarInscricaoPresencial(
  codigo: string,
): Promise<RetornoValidacao> {
  const { data, error } = await supabase.rpc('validar_inscricao_presencial', {
    p_codigo: codigo,
  });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  return {
    ok: o.ok === true,
    situacao: (txt(o.situacao) as ResultadoValidacao) || 'nao_encontrada',
    ordem: num(o.ordem),
    posicao_espera: num(o.posicao_espera),
    nome: txt(o.nome),
    idade: num(o.idade),
    foto_url: txt(o.foto_url),
    grupo: txt(o.grupo),
    modalidade: txt(o.modalidade),
    vagas_usadas: num(o.vagas_usadas),
    vagas_total: num(o.vagas_total),
    mensagem: txt(o.mensagem),
  };
}

// `presencial_painel` — números por modalidade/grupo e as últimas bipagens.
export async function carregarPainelPresencial(
  eventoId: number,
): Promise<PainelPresencial> {
  const { data, error } = await supabase.rpc('presencial_painel', { p_evento: eventoId });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  const modalidades = (Array.isArray(o.modalidades) ? o.modalidades : []).map((raw) => {
    const m = (raw || {}) as Record<string, unknown>;
    return {
      id: Number(m.id) || 0,
      nome: txt(m.nome) || '',
      vagas_total: num(m.vagas_total),
      com_vaga: Number(m.com_vaga) || 0,
      lista_espera: Number(m.lista_espera) || 0,
      aguardando: Number(m.aguardando) || 0,
      usa_senha: m.usa_senha === true,
      grupos: (Array.isArray(m.grupos) ? m.grupos : []).map((graw) => {
        const g = (graw || {}) as Record<string, unknown>;
        return {
          id: Number(g.id) || 0,
          nome: txt(g.nome) || '',
          vagas: num(g.vagas),
          com_vaga: Number(g.com_vaga) || 0,
          aguardando: Number(g.aguardando) || 0,
        };
      }),
    };
  });
  const ultimas = (Array.isArray(o.ultimas) ? o.ultimas : []).map((raw) => {
    const u = (raw || {}) as Record<string, unknown>;
    return {
      ordem: num(u.ordem),
      nome: txt(u.nome),
      grupo: txt(u.grupo),
      situacao: txt(u.situacao),
      hora: txt(u.hora),
      senha: txt(u.senha),
    };
  });
  return { modalidades, ultimas };
}

// `presencial_aguardando` — quem ainda não foi bipado (busca por nome ou CPF).
export async function buscarAguardando(
  eventoId: number,
  busca: string,
): Promise<AguardandoLinha[]> {
  const { data, error } = await supabase.rpc('presencial_aguardando', {
    p_evento: eventoId,
    p_busca: busca ?? '',
  });
  if (error) throw error;
  if (!Array.isArray(data)) return [];
  return (data as Record<string, unknown>[]).map((o) => ({
    codigo: String(o.codigo ?? ''),
    nome: txt(o.nome) || '',
    grupo: txt(o.grupo),
    modalidade: txt(o.modalidade),
    idade: num(o.idade),
    cpf_final: txt(o.cpf_final),
    inscrito_as: txt(o.inscrito_as),
  }));
}

// `cancelar_nao_validadas` — cancela as fichas não terminadas e sobe os
// suplentes (na ordem da senha) para as vagas que sobraram.
export interface CancelamentoResultado {
  canceladas: number;
  promovidos: number;
}

export async function cancelarNaoValidadas(
  modalidadeId: number,
): Promise<CancelamentoResultado> {
  const { data, error } = await supabase.rpc('cancelar_nao_validadas', {
    p_modalidade: modalidadeId,
  });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  if (o.ok !== true) throw new Error('Não foi possível cancelar agora.');
  return {
    canceladas: Number(o.canceladas) || 0,
    promovidos: Number(o.promovidos) || 0,
  };
}

// ---------------------------------------------------------------------------
// Fila pela senha — modalidades que entram pela senha escrita no papel (sem QR).
// ---------------------------------------------------------------------------
export type SituacaoItemFila = 'vaga' | 'suplente' | 'aguardando';

export interface ItemFila {
  inscricao_id: number;
  codigo: string;
  nome: string;
  idade: number | null;
  senha: string | null;
  situacao: SituacaoItemFila;
  status: string | null;
  valor: number | null;
  pago: number | null;
  falta: number | null;
  alertas: string[];
  posicao: number | null;
}

export interface GrupoFila {
  id: number;
  nome: string;
  vagas: number | null;
  com_vaga: number;
  pagos: number;
  suplentes: number;
  aguardando: number;
  itens: ItemFila[];
}

export interface FilaPorSenha {
  usa_senha: boolean;
  grupos: GrupoFila[];
}

// `fila_por_senha` — fila de uma modalidade que usa a senha. Só a direção.
export async function carregarFilaPorSenha(modalidadeId: number): Promise<FilaPorSenha> {
  const { data, error } = await supabase.rpc('fila_por_senha', {
    p_modalidade: modalidadeId,
  });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  const grupos = (Array.isArray(o.grupos) ? o.grupos : []).map((graw) => {
    const g = (graw || {}) as Record<string, unknown>;
    return {
      id: Number(g.id) || 0,
      nome: txt(g.nome) || '',
      vagas: num(g.vagas),
      com_vaga: Number(g.com_vaga) || 0,
      pagos: Number(g.pagos) || 0,
      suplentes: Number(g.suplentes) || 0,
      aguardando: Number(g.aguardando) || 0,
      itens: (Array.isArray(g.itens) ? g.itens : []).map((iraw) => {
        const it = (iraw || {}) as Record<string, unknown>;
        return {
          inscricao_id: Number(it.inscricao_id) || 0,
          codigo: String(it.codigo ?? ''),
          nome: txt(it.nome) || '',
          idade: num(it.idade),
          senha: txt(it.senha),
          situacao: (txt(it.situacao) as SituacaoItemFila) || 'aguardando',
          status: txt(it.status),
          valor: num(it.valor),
          pago: num(it.pago),
          falta: num(it.falta),
          alertas: Array.isArray(it.alertas) ? (it.alertas as unknown[]).map(String) : [],
          posicao: num(it.posicao),
        };
      }),
    };
  });
  return { usa_senha: o.usa_senha === true, grupos };
}

// `corrigir_senha_fila` — só a direção. Corrige a senha de uma inscrição.
export async function corrigirSenhaFila(
  inscricaoId: number,
  senha: string,
): Promise<{ senha: string | null; status: string | null }> {
  const { data, error } = await supabase.rpc('corrigir_senha_fila', {
    p_inscricao: inscricaoId,
    p_senha: senha,
  });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  if (o.ok !== true) throw new Error('Não foi possível corrigir a senha.');
  return { senha: txt(o.senha), status: txt(o.status) };
}

// Liga/desliga o modo presencial de uma modalidade (só quem organiza).
export async function configurarPresencial(
  modalidadeId: number,
  ligado: boolean,
): Promise<{ ligado: boolean; ja_inscritos_validados: number }> {
  const { data, error } = await supabase.rpc('configurar_presencial', {
    p_modalidade: modalidadeId,
    p_ligado: ligado,
  });
  if (error) throw error;
  const o = (data || {}) as Record<string, unknown>;
  if (o.ok === false) throw new Error('Não foi possível mudar agora.');
  return {
    ligado: o.ligado === true,
    ja_inscritos_validados: Number(o.ja_inscritos_validados) || 0,
  };
}

// O evento tem alguma modalidade presencial? (para mostrar a entrada do QR).
export async function eventoEhPresencial(eventoId: number): Promise<boolean> {
  const { data, error } = await supabase
    .from('modalidades')
    .select('id')
    .eq('evento_id', eventoId)
    .eq('validacao_presencial', true)
    .limit(1);
  if (error) return false;
  return ((data as unknown[]) || []).length > 0;
}